import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import AddNewExpensesConfirmationModal from 'components/modals/invoices/AddNewExpensesConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { AddNewInvoiceItems } from 'pages/invoices/hooks/useAddNewInvoiceItems';
import { DetailsTabSuccessMessages } from 'pages/invoices/InvoicePage';
import { InvoiceAlertId } from 'pages/invoices/util/invoiceTabAlerts';
import { Dispatch, ReactNode, SetStateAction, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import { useUpdateTimecardsHourlyRatesMutation } from 'store/api/invoice-details';
import { LimitType } from 'types/invoice-details';
import { InvoiceData } from 'types/invoices';
import { AlertActionButton, DetailsAlert } from './Details';
import { DIALOG_448 } from 'components/modals/dialogWidths';

interface IncludeItems {
    kind: 'timecards' | 'expenses';
    titleId: string;
    chooseId: string;
    prevMonthId: string;
    thisMonthId: string;
}

const INCLUDE_TIMECARDS: IncludeItems = {
    kind: 'timecards',
    titleId: 'invoices.actions.include-timecards',
    chooseId: 'invoices.actions.choose-which-timecards',
    prevMonthId: 'invoices.actions.add-new-timecards-prev-month-confirm',
    thisMonthId: 'invoices.actions.add-new-timecards-this-month-confirm',
};

const INCLUDE_EXPENSES: IncludeItems = {
    kind: 'expenses',
    titleId: 'invoices.actions.include-expenses',
    chooseId: 'invoices.actions.choose-which-expenses',
    prevMonthId: 'invoices.actions.add-new-expenses-prev-month-confirm',
    thisMonthId: 'invoices.actions.add-new-expenses-this-month-confirm',
};

interface Args {
    invoice: InvoiceData;
    addNewInvoiceItems: AddNewInvoiceItems;
    setSuccessMessages: Dispatch<SetStateAction<DetailsTabSuccessMessages>>;
    addedTimecardCount: number;
    addedExpenseCount: number;
}

/**
 * The invoice's item alerts as they are drawn, by the id `getInvoiceTabAlerts` gives them: which ones show, and in
 * what order, is that function's. The case's own alert (a changed fee arrangement) stays with its case. A message of
 * several sentences is split in the new design: its first sentence the title (newDesign.json), the rest the
 * description, which classic does not draw.
 */
export const useInvoiceAlertNodes = ({
    invoice,
    addNewInvoiceItems,
    setSuccessMessages,
    addedTimecardCount,
    addedExpenseCount,
}: Args) => {
    const { handleAddNewTimecards, handleAddNewExpenses } = addNewInvoiceItems;
    const { openModal, closeModal } = useModal();
    const buttonsDisabled = useSelector((state) => state.buttons.buttonsDisabled);
    const [updateTimecardsHourlyRates, { isLoading: isUpdatingTimecardsHourlyRates }] =
        useUpdateTimecardsHourlyRatesMutation();

    const handleUpdatedInvoiceTimecardsHourlyRates = useCallback(
        async (invoiceId: number) => {
            if (!invoice.id) {
                return;
            }

            await updateTimecardsHourlyRates({ invoiceId })
                .unwrap()
                .then(() => {
                    setSuccessMessages((prev) => ({ ...prev, hourlyRateUpdated: true }));
                })
                .catch(console.error);
        },
        [invoice.id, setSuccessMessages, updateTimecardsHourlyRates]
    );

    const missingHourlyRateEmployeeNames = invoice.missingHourlyRateEmployeeNames ?? [];
    const unsignedServiceAgreementMatters = (invoice.invoiceMatters ?? []).filter(
        (item) => item.hasUnsignedServiceAgreement
    );

    /** Asks which uninvoiced items to add: a choice of month when both months have some, else a confirmation. */
    const openIncludeItems = ({ titleId, chooseId, prevMonthId, thisMonthId, kind }: IncludeItems) => {
        const add = kind === 'timecards' ? handleAddNewTimecards : handleAddNewExpenses;
        const hasPrevMonth =
            kind === 'timecards' ? invoice.hasUninvoicedTimecardsPrevMonth : invoice.hasUninvoicedExpensesPrevMonth;
        const hasThisMonth =
            kind === 'timecards' ? invoice.hasUninvoicedTimecardsThisMonth : invoice.hasUninvoicedExpensesThisMonth;
        if (hasPrevMonth && hasThisMonth) {
            openModal(
                <AddNewExpensesConfirmationModal
                    titleId={titleId}
                    descriptionId={chooseId}
                    onCancel={closeModal}
                    onConfirm={(value) => {
                        void add(value, invoice);
                    }}
                />,
                closeModal,
                { width: 400 },
                undefined,
                DIALOG_448
            );
            return;
        }
        const message = <FormattedMessage id={hasPrevMonth ? prevMonthId : thisMonthId} />;
        openModal(
            <ConfirmationModal
                titleId={titleId}
                onCancel={closeModal}
                onConfirm={() => {
                    void add(LimitType.ALL, invoice);
                }}
                content={<p className="text-body1">{message}</p>}
                alert={{ titleId, description: message, confirmId: titleId }}
            />,
            closeModal,
            { width: 400 }
        );
    };

    const nodes: Partial<Record<InvoiceAlertId, ReactNode>> = {
        'missing-job-role': (
            <DetailsAlert key="isTimecardEmployeeMissingRoleError" tone="warning">
                <FormattedMessage id="invoices.alerts.timecard-employee-missing-role" />
            </DetailsAlert>
        ),
        'missing-hourly-rate': (
            <DetailsAlert key="isTimecardMissingHourlyRateWarning" tone="warning">
                {missingHourlyRateEmployeeNames.length > 0 ? (
                    <FormattedMessage
                        id="invoices.alerts.timecard-missing-hourly-rate"
                        values={{ employees: missingHourlyRateEmployeeNames.join(', ') }}
                    />
                ) : (
                    <FormattedMessage id="invoices.alerts.timecard-missing-hourly-rate-unnamed" />
                )}
            </DetailsAlert>
        ),
        'cap-error': (
            <DetailsAlert
                key="matterCapError"
                tone="error"
                description={<FormattedMessage id="invoices.alerts.please-adjust-cap-description" />}
            >
                <FormattedMessage id="invoices.alerts.please-adjust-cap" />
            </DetailsAlert>
        ),
        'unsigned-service-agreement': (
            <DetailsAlert
                key="unsignedServiceAgreement"
                tone="error"
                description={<FormattedMessage id="invoices.alerts.unsigned-service-agreement-description" />}
            >
                <FormattedMessage
                    id="invoices.alerts.unsigned-service-agreement"
                    values={{ cases: unsignedServiceAgreementMatters.map((m) => m.matterNumber).join(', ') }}
                />
            </DetailsAlert>
        ),
        'new-timecards': (
            <DetailsAlert
                key="uninvoicedTimecards-warning"
                tone="warning"
                action={
                    <AlertActionButton onClick={() => openIncludeItems(INCLUDE_TIMECARDS)} disabled={buttonsDisabled}>
                        <FormattedMessage id="invoices.actions.add-new-timecards" />
                    </AlertActionButton>
                }
            >
                <FormattedMessage id="invoices.alerts.new-timecards" />
            </DetailsAlert>
        ),
        'cannot-add-new-items': (
            <DetailsAlert
                key="uninvoicedItems-info"
                tone="info"
                description={<FormattedMessage id="invoices.alerts.cannot-add-new-items-description" />}
            >
                <FormattedMessage id="invoices.alerts.cannot-add-new-items" />
            </DetailsAlert>
        ),
        'new-expenses': (
            <DetailsAlert
                key="uninvoicedExpenses-warning"
                tone="warning"
                action={
                    <AlertActionButton onClick={() => openIncludeItems(INCLUDE_EXPENSES)} disabled={buttonsDisabled}>
                        <FormattedMessage id="invoices.actions.add-new-expenses" />
                    </AlertActionButton>
                }
            >
                <FormattedMessage id="invoices.alerts.new-expenses" />
            </DetailsAlert>
        ),
        'hourly-rates-changed': (
            <DetailsAlert
                key="matterHourlyPriceChanged-warning"
                tone="warning"
                action={
                    <AlertActionButton
                        onClick={() => {
                            void handleUpdatedInvoiceTimecardsHourlyRates(invoice.id);
                        }}
                        disabled={buttonsDisabled}
                    >
                        <FormattedMessage id="invoices.actions.apply-new-hourly-rates" />
                    </AlertActionButton>
                }
            >
                <FormattedMessage id="invoices.alerts.hourly-rates-not-relevant" />
            </DetailsAlert>
        ),
        'cannot-update-hourly-rates': (
            <DetailsAlert
                key="matterHourlyPriceChanged-info"
                tone="info"
                description={<FormattedMessage id="invoices.alerts.cannot-update-timecards-hourly-rates-description" />}
            >
                <FormattedMessage id="invoices.alerts.cannot-update-timecards-hourly-rates" />
            </DetailsAlert>
        ),
        'timecards-added': (
            <DetailsAlert key="new-timecards-added" tone="success">
                <FormattedMessage values={{ count: addedTimecardCount }} id="invoices.alerts.new-timecards-added" />
            </DetailsAlert>
        ),
        'expenses-added': (
            <DetailsAlert key="new-expenses-added" tone="success">
                <FormattedMessage values={{ count: addedExpenseCount }} id="invoices.alerts.new-expenses-added" />
            </DetailsAlert>
        ),
        'hourly-rates-updated': (
            <DetailsAlert key="invoice-timecards-hourly-rate-updated" tone="success">
                <FormattedMessage id="invoices.alerts.hourly-rates-updated" />
            </DetailsAlert>
        ),
    };

    return { nodes, isUpdatingTimecardsHourlyRates };
};
